import { MouseEvent, useCallback, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getMMBots, setAlgorithmMode } from "../../api/mmApi";
import { AlgorithmMode } from "../../pages/inner/mmTypes";

interface Props {
    connectionId: string;
    pairLabel: string;
    isConnectionActive: boolean;
}

const MODE_LABELS: Record<AlgorithmMode, string> = {
    legacy: "Legacy",
    lifecycle_v2_shadow: "Lifecycle V2 · Shadow",
    lifecycle_v2_live: "Lifecycle V2 · Live",
};

const MODE_COLORS: Record<AlgorithmMode, "default" | "info" | "success"> = {
    legacy: "default",
    lifecycle_v2_shadow: "info",
    lifecycle_v2_live: "success",
};

function apiErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        if (typeof detail === "string") return detail;
        if (detail) return JSON.stringify(detail);
    }
    return "Не удалось переключить алгоритм. Проверьте состояние пары и повторите попытку.";
}

export function AlgorithmModeControl({ connectionId, pairLabel, isConnectionActive }: Props) {
    const [open, setOpen] = useState(false);
    const [liveConfirmationOpen, setLiveConfirmationOpen] = useState(false);
    const [mode, setMode] = useState<AlgorithmMode | null>(null);
    const [loading, setLoading] = useState(false);
    const [switching, setSwitching] = useState<AlgorithmMode | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const refreshMode = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const bot = await getMMBots(connectionId);
            setMode(bot.algorithm_mode ?? "legacy");
        } catch (requestError) {
            setError(apiErrorMessage(requestError));
        } finally {
            setLoading(false);
        }
    }, [connectionId]);

    const handleOpen = async (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setOpen(true);
        setSuccess(null);
        await refreshMode();
    };

    const switchMode = async (nextMode: AlgorithmMode, reason: string) => {
        setSwitching(nextMode);
        setError(null);
        setSuccess(null);
        try {
            const bot = await setAlgorithmMode(connectionId, nextMode, reason);
            const actualMode = bot.algorithm_mode ?? nextMode;
            setMode(actualMode);
            setSuccess(`Режим изменён: ${MODE_LABELS[actualMode]}`);
            return true;
        } catch (requestError) {
            setError(apiErrorMessage(requestError));
            return false;
        } finally {
            setSwitching(null);
        }
    };

    const enableLive = async () => {
        const changed = await switchMode(
            "lifecycle_v2_live",
            "Lifecycle V2 live enabled from admin panel after shadow review",
        );
        if (changed) setLiveConfirmationOpen(false);
    };

    const busy = loading || switching !== null;

    return (
        <>
            <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<AccountTreeIcon />}
                onClick={handleOpen}
            >
                Алгоритм
            </Button>

            <Dialog
                open={open}
                onClose={() => !busy && setOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Алгоритм · {pairLabel}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2.25}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Текущий режим
                            </Typography>
                            {loading || mode === null ? (
                                <CircularProgress size={24} />
                            ) : (
                                <Chip
                                    label={MODE_LABELS[mode]}
                                    color={MODE_COLORS[mode]}
                                    variant={mode === "legacy" ? "outlined" : "filled"}
                                />
                            )}
                        </Box>

                        {!isConnectionActive && mode !== "lifecycle_v2_live" && (
                            <Alert severity="warning">
                                Пара сейчас Offline. Shadow начнёт собирать решения только после включения пары.
                            </Alert>
                        )}
                        {error && <Alert severity="error">{error}</Alert>}
                        {success && <Alert severity="success">{success}</Alert>}

                        <Divider />

                        <Stack spacing={1}>
                            <Typography variant="subtitle2">1. Безопасное наблюдение</Typography>
                            <Typography variant="body2" color="text.secondary">
                                В Shadow реальные ордера продолжает создавать Legacy. V2 только рассчитывает и сохраняет решения.
                            </Typography>
                            <Button
                                variant={mode === "lifecycle_v2_shadow" ? "contained" : "outlined"}
                                color="info"
                                disabled={busy || mode === "lifecycle_v2_shadow"}
                                onClick={() => switchMode(
                                    "lifecycle_v2_shadow",
                                    "Lifecycle V2 shadow enabled from admin panel",
                                )}
                            >
                                {switching === "lifecycle_v2_shadow" ? "Переключение…" : "Включить Shadow"}
                            </Button>
                        </Stack>

                        <Stack spacing={1}>
                            <Typography variant="subtitle2">2. Реальная работа V2</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Live доступен только после Shadow и появления сохранённого V2-state. Реальные решения начнёт исполнять V2.
                            </Typography>
                            <Button
                                variant="contained"
                                color="success"
                                disabled={busy || mode !== "lifecycle_v2_shadow"}
                                onClick={() => setLiveConfirmationOpen(true)}
                            >
                                Включить Live
                            </Button>
                        </Stack>

                        <Divider />

                        <Stack spacing={1}>
                            <Typography variant="subtitle2" color="error">Аварийный откат</Typography>
                            <Button
                                variant="contained"
                                color="error"
                                disabled={busy || mode === "legacy"}
                                onClick={() => switchMode(
                                    "legacy",
                                    "Emergency rollback to legacy from admin panel",
                                )}
                            >
                                {switching === "legacy" ? "Rollback…" : "Rollback в Legacy"}
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={refreshMode} disabled={busy}>Обновить</Button>
                    <Button onClick={() => setOpen(false)} disabled={busy}>Закрыть</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={liveConfirmationOpen}
                onClose={() => switching === null && setLiveConfirmationOpen(false)}
            >
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <WarningAmberIcon color="warning" />
                        <span>Включить Lifecycle V2 Live?</span>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Пара <strong>{pairLabel}</strong> начнёт исполнять реальные решения нового алгоритма.
                        Legacy останется прогретым только для rollback.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLiveConfirmationOpen(false)} disabled={switching !== null}>
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={enableLive}
                        disabled={switching !== null}
                    >
                        {switching === "lifecycle_v2_live" ? "Переключение…" : "Подтвердить Live"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
